import React, { useState, memo } from 'react';
import moment from 'moment';
import { Button } from 'primereact/button';
import ReactPlayer from 'react-player';
import { LibraryModel } from 'models/library.model';
import './index.css';

interface UploadProps {
  uploadType: 'file' | 'folder' | 'root';
  folderUpload: string;
  updateMediaSuccess: (file: LibraryModel) => void;
  toast?: React.RefObject<any>;
  setShowLoading?: (loading: boolean) => void;
  setFile: (file: File, folder: string) => Promise<any>;
}

const UploadComponent: React.FC<UploadProps> = ({ uploadType, folderUpload, updateMediaSuccess, toast, setShowLoading, setFile }) => {
  const [progress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInfo, setFileInfo] = useState<LibraryModel>(new LibraryModel());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    setShowLoading?.(true);
    try {
      const res = await setFile(selectedFile, '');
      const finalFile = { ...fileInfo, url: res.data.data };
      updateMediaSuccess(finalFile);
    } catch (err) {
      console.log(err);
      // optionally toast error
    } finally {
      setShowLoading?.(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      const size = file.size;

      // validate
      if (file.type.includes('image') && size > 5 * 1024 * 1024) {
        toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Chọn file ảnh kích thước tối đa là 5MB', life: 3000 });
        return;
      }
      if (file.type.includes('video') && size > 1024 * 1024 * 1024) {
        toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Chọn video tối đa là 1024MB', life: 3000 });
        return;
      }
      if (!file.type.includes('image') && !file.type.includes('video') && size > 100 * 1024 * 1024) {
        toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Chọn file tối đa là 100MB', life: 3000 });
        return;
      }

      setShowLoading?.(true);
      const reader = new FileReader();
      const image = new Image();
      reader.readAsDataURL(file);
      if(file.type.includes('image'))
      {
        image.src = URL.createObjectURL(file);

        image.onload = () => {
          setFileInfo({
            ...fileInfo,
            url: reader.result as string,
            name: file.name,
            size: file.size.toString(),
            type: file.type.startsWith('image') ? 'image' :
                  file.type.startsWith('video') ? 'video' :
                  file.type.startsWith('audio') ? 'audio' : 'link',
            width: image.width,
            height: image.height,
            parentId: folderUpload,
            createDate: moment().format('YYYY-MM-DD HH:mm:ss'),
            modifiedDate: moment().format('YYYY-MM-DD HH:mm:ss')
          });
          
        };
      }
      else
      {
          setFileInfo({
            ...fileInfo,
            url: reader.result as string,
            name: file.name,
            size: file.size.toString(),
            type: file.type.startsWith('image') ? 'image' :
                  file.type.startsWith('video') ? 'video' :
                  file.type.startsWith('audio') ? 'audio' : 'link',
            width: 0,
            height: 0,
            parentId: folderUpload,
            createDate: moment().format('YYYY-MM-DD HH:mm:ss'),
            modifiedDate: moment().format('YYYY-MM-DD HH:mm:ss')
          });
      }
      setSelectedFile(file);
      setShowLoading?.(false);
    } catch (err) {
      console.log(err);
      setShowLoading?.(false);
    }
  };

  if (uploadType === 'file') {
    return (
      <div className="upload-media">
        <form className="grid mt-1" onSubmit={handleSubmit}>
          <div className="col-5">
            <label htmlFor="updatefile">
              {fileInfo.type?.includes('image') ? (
                <img src={fileInfo.url || './images/common/empty-image.png'} alt="img" style={{ width: '100%' }} />
              ) : fileInfo.type?.includes('video') ? (
                <ReactPlayer
                  url={fileInfo.url}
                  controls
                  loop
                  width="100%"
                  config={{ youtube: { playerVars: { showinfo: 1 } }, facebook: { appId: '12345' } }}
                />
              ) : (
                <img src="./images/common/empty-image.png" alt="img" style={{ width: '100%' }} />
              )}
              <div className="text-center text-blue-600 pointer">Tải file lên</div>
            </label>
            <input type="file" id="updatefile" hidden onChange={handleFileChange} />
          </div>

          <div className="col-7 pl-1">
            <b>Thông tin File</b>
            <p> Tên File: {fileInfo.name || ''}</p>
            <p> Chiều cao: {fileInfo.height || 0} px</p>
            <p> Chiều rộng: {fileInfo.width || 0} px</p>
            <p> Loại file: {fileInfo.type || ''}</p>
            <p> Kích thước: {Math.round((+fileInfo.size || 0) / 1024 / 1024 * 100) / 100} mb</p>
            <Button label="Lưu lại" type="submit" className="p-button-sm" />
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="grid">
      <div className="form-group col-12">
        <label htmlFor="inputtext">Tên thư mục</label>
        <input
          type="text"
          placeholder="Nhập tên thư mục"
          id="inputtext"
          className="form-control"
          value={fileInfo.name || ''}
          onChange={(e) => setFileInfo({ ...fileInfo, name: e.target.value })}
        />
      </div>
      <div className="col-12 text-right">
        <Button
          label="Lưu lại"
          icon="pi pi-save"
          onClick={() => {
            if (uploadType === 'folder' || uploadType === 'root') {
              fileInfo.name
                ? updateMediaSuccess(fileInfo)
                : toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Vui lòng nhập tên thư mục', life: 3000 });
            }
          }}
          className="p-button-sm"
        />
      </div>
    </div>
  );
};

export default memo(UploadComponent);
